import { useState, useEffect, useCallback, useRef } from "react"
import seqStore from "../store/seqStore"
import musicalScale from "../assets/musicalScale"
import PlayButton from "../components/inputs/PlayButton"
import StopButton from "../components/inputs/StopButton"
import Tempo from "../components/inputs/Tempo"
import Division from "../components/inputs/Division" 
import Scales from "../components/inputs/Scales"
import MidiChannel from "../components/inputs/MidiChannel"
import MidiOutSelect from "../components/inputs/MidiOutSelect"
import MidiInSelect from "../components/inputs/MidiInSelect"
import Pulses from "../components/inputs/Pulses"
import Length from "../components/inputs/Length"
import Offset from "../components/inputs/Offset"
import Direction from "../components/inputs/Direction"
import Transpose from "../components/inputs/Transpose"
import Octave from "../components/inputs/Octave"
import Keyboard from "../components/inputs/Keyboard"
import RecButton from "../components/inputs/RecButton"
import playheadCounter from "../utils/playheadCounter"
import genEuclidean from "../utils/genEuclidean"
import GateLength from "../components/inputs/GateLength"
import RootNote from "../components/inputs/RootNote"
import SavePreset from "../components/inputs/SavePreset"
import TracksMonitor from "../components/sequencer/TracksMonitor"
import MelodyIcon from "../components/icons/MelodyIcon"
import EuclideanIcon from "../components/icons/EuclideanIcon"
import RoutingIcon from "../components/icons/RoutingIcon"
import SequenceIcon from "../components/icons/SequenceIcon"
import ChevronIcon from "../components/icons/ChevronIcon"
import Sequencer from "../components/sequencer/Sequencer"
import KeyboardIcon  from "../components/icons/KeyboardIcon"
import MetronomeIcon from "../components/icons/MetronomeIcon"
import tickSound from '../assets/metronomeTick.mp3'
import tockSound from '../assets/metronomeTock.mp3'
import tick from '../assets/tick.mp3'

export default function Home() {
    // local states
    const [isRecording, setIsRecording] = useState(false)//__________record button state
    const [isPlaying, setIsPlaying] = useState(false)//_____________play button state
    const [prevKeyPress, setPrevKeyPress] = useState(0)//____________debounce for space bar key
    const [isOptionsOpen, setIsOptionsOpen] = useState(false)//______options menu state dropdown
    const [isEuclideanOpen, setIsEuclideanOpen] = useState(false)//__euclidean menu state dropdown
    const [MIDIOutput, setMIDIOutput] = useState(null)//_____________MIDI output
    const [MIDIInput, setMIDIInput] = useState(null)//_____________MIDI output
    const [tickDuration, setTickDuration] = useState([20.833333333333332, 20.833333333333332, 20.833333333333332, 20.833333333333332])//_________MIDI tick duration
    const [stepDuration, setStepDuration] = useState([125, 125, 125, 125])//_________step duration calculated from the tick
    const [gateLength, setGateLength] = useState([62.5, 62.5, 62.5, 62.5])//_____________gate length calculated from the step duration
    const [sequenceMenuState, setSequenceMenuState] = useState(false)//__________________sequence menu toggle state
    const [keyboardMenuState, setKeyboardMenuState] = useState(false)//__________________keyboard menu toggle state
    const [metronome, setMetronome] = useState(0)//__________________metronome state
    const [isMetronomePlay, setIsMetronomePlay] = useState(false)//__________________is metronome playing?

    // store states
    const currentTrack = seqStore((state) => state.settings.currentTrack)
    const mute = seqStore((state) => state.settings.mute)
    const euclideanPattern = seqStore((state) => state.settings.euclideanPattern)//___________get euclidean rhythm pattern
    const setEuclideanPattern = seqStore((state) => state.setEuclideanPattern)//_____store euclidean rhythm pattern                     
    const tempo = seqStore((state) => state.tempo)//_________________________get main tempo
    const melody = seqStore((state) => state.melody)//_____________________________get melody unscaled
    const melody_scale = seqStore((state) => state.melody_scale)//______________________store melody unscaled
    const setNote_scale = seqStore((state) => state.setNote_scale)//_________________store scaled melody 
    const ratchetPattern = seqStore((state) => state.settings.ratchetPattern)//______get ratchet
    const rootNote = seqStore((state) => state.settings.root)//______________get root note
    const length = seqStore((state) => state.settings.length)//______________get length of the sequence
    const scale = seqStore((state) => state.settings.scale)//________________get scale
    const pulses = seqStore((state) => state.settings.pulses)//______________get number of active step
    const offset = seqStore((state) => state.settings.offset)//______________get offset of the sequence
    const gatePercentage = seqStore((state) => state.settings.gatePercentage)//__get gate percentage
    const semitone = seqStore((state) => state.settings.semitone)//_________get transpose in semitone
    const octave = seqStore((state) => state.settings.octave)//______________get transpose in octave
    const division = seqStore((state) => state.settings.division)//__________get division
    const direction = seqStore((state) => state.settings.direction)//________get direction
    const midiCh = seqStore((state) => state.settings.midiCh)//______________get midi channel
    const playhead = seqStore((state) => state.settings.playhead)//__________get current playhead
    const setPlayhead = seqStore((state) => state.setPlayhead)//_____________________set current playhead

    const playheadRef = useRef(playhead)

    // various variables
    const TRACKS = 4; // number of tracks
    const NOTE_ON = '0x9' //MIDI message code on chan1
    const NOTE_OFF = '0x8' //MIDI message code on chan1
    // eslint-disable-next-line
    const CLOCK = '0xF8'
    // eslint-disable-next-line
    const START = '0xFA'
    // eslint-disable-next-line
    const CONTINUE = '0xFB' 
    // eslint-disable-next-line
    const STOP = '0xFC'
    const CC = '0xB' // MIDI message code for CONTROL CHANGE + MIDICH[midiCh]
    const ALLSOUNDOFF = '0x78'
    const MIDICH = [
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f'
    ]
    const scaleNames = [
        'chromatic', 'major', 'minor', 'phrygian', 'lydian', 'mixolydian', 'locrian'
    ]
    const modeIntervals = {
        major: [4, 7, 11], // Major Third, Perfect Fifth, Major Seventh
        minor: [3, 7, 10], // Minor Third, Perfect Fifth, Minor Seventh
        phrygian: [1, 8, 10], // Minor Second, Minor Sixth, Minor Seventh
        lydian: [4, 6, 11], // Major Third, Augmented Fourth, Major Seventh
        mixolydian: [4, 7, 10], // Major Third, Perfect Fifth, Minor Seventh
        locrian: [1, 6, 10] // Minor Second, Diminished Fifth, Diminished Seventh
    }

    /*const audio = new Audio(metronomeTick)
    audio.loop = false*/

    /***********************/
    /* calculate durations */
    /***********************/
    /*
    useEffect(() => {
        const calculateDurations = () => {
          const newTickDuration = Array(TRACKS).fill(null)
          const newStepDuration = Array(TRACKS).fill(null)
          const newGateLength = Array(TRACKS).fill(null)
      
          for (let i = 0; i < TRACKS; i++) {
            if (division[i] !== null && tempo !== null) {
              const tempTick = 60000 / (tempo * 24)
      
              // Calculate step duration based on division
              let tempStep
              switch (division[i]) {
                case 6:
                  tempStep = tempTick * 1.5
                  break
                case 5:
                  tempStep = tempTick * 3
                  break
                case 4:
                  tempStep = tempTick * 6
                  break
                case 3:
                  tempStep = tempTick * 12
                  break
                case 2:
                  tempStep = tempTick * 24
                  break
                case 1:
                  tempStep = tempTick * 48
                  break
                case 0:
                  tempStep = tempTick * 96
                  break
                default:
                  tempStep = tempTick * 96
              }
      
              newTickDuration[i] = tempTick
              newStepDuration[i] = tempStep
              newGateLength[i] = (tempStep / 100) * gatePercentage[i]
            }
          }
      
          setTickDuration(newTickDuration)
          setStepDuration(newStepDuration)
          setGateLength(newGateLength)
        }
      
        calculateDurations()
      }, [tempo, gatePercentage, division])
      */

    /**********************/
    /* Find gate duration */
    /**********************/
    /*
    useEffect(() => {
        const calculateGateLength = () => {
          const fragStepDuration = stepDuration[currentTrack] / 100 // 1% of the step duration
          const newLength = fragStepDuration * gatePercentage[currentTrack]
      
          if (newLength !== gateLength[currentTrack]) {
            const updatedGateLength = [...gateLength]
            updatedGateLength[currentTrack] = newLength
            setGateLength(updatedGateLength)
          }
        }
      
        calculateGateLength()
      }, [gatePercentage, gateLength, stepDuration, currentTrack])
      */

    /*// send CLOCK, START, STOP, CONTINUE
    useEffect(() => {
        let intervalTick
        let playSent = false
    
        const clockSync = () => {
            intervalTick = setInterval(() => {
                MIDIOutput.send([CLOCK])
            }, tickDuration)
        }

        const startSync = () => {
            if (!playSent) {
                MIDIOutput.send([START])
                console.log('start synchro')
                playSent = true
                clockSync()
            } else {
                return
            }
        }
    
        const pauseSync = () => {
            MIDIOutput.send([CONTINUE])
            console.log('pause synchro')
            clearInterval(intervalTick)
        }
    
        const stopSync = () => {
            MIDIOutput.send([STOP])
            console.log('stop synchro')
            clearInterval(intervalTick)
            playSent = false
        }
    
        if (MIDIOutput) {
            if (isPlaying) {
                startSync()
            } else {
                stopSync()
            }
        }
    
        return () => {
            clearInterval(intervalTick) 
        }
    }, [isPlaying, tempo, MIDIOutput])*/

    /*******************************/
    /* calculate euclidean pattern */
    /*******************************/
    useEffect(() => {
        const calculateEuclideanPattern = () => {
          const pattern = genEuclidean(pulses[currentTrack], length[currentTrack], offset[currentTrack])
          setEuclideanPattern(currentTrack, pattern)
        }
        calculateEuclideanPattern()
      }, [pulses, length, offset, currentTrack, genEuclidean, setEuclideanPattern])

    /**********************/
    /* interval mechanics */
    /**********************/
    /*
    useEffect(() => {
        let intervalId

        // Function to handle playing a sequence for all tracks
        const playSequence = () => {
            for (let i = 0; i < TRACKS; i++) {
                if (MIDIOutput && isPlaying) {
                    let tempPlayhead
                    // If sequencer was in a stop mode, the next playhead will be 0
                    if (playhead[i] === -1) {
                        tempPlayhead = 0
                        setPlayhead(i, tempPlayhead)
                    } else {
                        // Move playhead ahead
                        tempPlayhead = playheadCounter(playhead[i], direction[i], length[i])
                        setPlayhead(i, tempPlayhead)
                    }

                    // If the step has an active pulse
                    if (euclideanPattern[i][tempPlayhead] === 1) {
                        // Define MIDI messages
                        const messageON = NOTE_ON + MIDICH[midiCh[i]]
                        const messageOFF = NOTE_OFF + MIDICH[midiCh[i]]
                        let note
                        if (!mute[i]) {
                            if (scale[i] !== 0) {
                                note = parseInt(melody_scale[i][tempPlayhead]) + (parseInt(octave[i]) * 12) + parseInt(semitone[i])
                                note = Math.max(0, Math.min(127, note))
                            } else {
                                note = parseInt(melody[i][tempPlayhead]) + (parseInt(octave[i]) * 12) + parseInt(semitone[i])
                                note = Math.max(0, Math.min(127, note))
                            }

                            const velocity = 0x7f // 127
                           
                            // Send initial note ON message
                            MIDIOutput.send([messageON, note, velocity])

                            if (ratchetPattern[i][tempPlayhead] > 0) {
                                let count
                                switch(ratchetPattern[i][tempPlayhead]) {
                                    case 1:
                                        count = 2
                                        break
                                    case 2:
                                        count = 3
                                        break
                                    case 3:
                                        count = 4
                                        break
                                }
                                
                                // Calculate additional note ON and note OFF messages for ratcheting
                                for (let j = 1; j <= ratchetPattern[i][tempPlayhead]; j++) {
                                    const delay = (stepDuration[i] / count) * j
                                    const timeON = performance.now() + delay
                                    const timeOFF = timeON + (stepDuration[i] / count)

                                    // Send additional note ON message
                                    MIDIOutput.send([messageON, note, velocity], timeON)
                                    // Send corresponding note OFF message
                                    MIDIOutput.send([messageOFF, note, 0], timeOFF)
                                }
                            } else if (ratchetPattern[i][tempPlayhead] < 0) {
                                let count
                                switch(ratchetPattern[i][tempPlayhead]) {
                                    case -1:
                                        count = 2
                                        break
                                    case -2:
                                        count = 3
                                        break
                                    case -3:
                                        count = 4
                                        break
                                }
                                
                                // Calculate additional note ON and note OFF messages for repeat
                                for (let j = 0; j <= count; j++) {
                                    const delay = (stepDuration[i]) * j //j = how many time to repeat
                                    const timeON = performance.now() + delay
                                    const timeOFF = timeON + (stepDuration[i] / count)

                                    // Send additional note ON message
                                    MIDIOutput.send([messageON, note, velocity], timeON)
                                    // Send corresponding note OFF message
                                    MIDIOutput.send([messageOFF, note, 0], timeOFF)
                                }
                            }
                        }
                        // Send final note OFF message after the full gate length
                        MIDIOutput.send([messageOFF, note, 0], performance.now() + gateLength[i])
                    }
                }
            }
        };

        if (isPlaying) {
            // Start the interval
            intervalId = setInterval(playSequence, Math.max(...stepDuration))
        }

        // Cleanup function to clear the interval
        return () => {
            clearInterval(intervalId)
        }
    }, [MIDIOutput, midiCh, melody, melody_scale, gateLength, MIDICH, octave, scale, semitone, stepDuration, isPlaying, playhead, direction, length, euclideanPattern, ratchetPattern, currentTrack])
    */ 
   
    
    // handle metronome 
    useEffect(() => {
        let requestId = null
        let counter = 0
        setMetronome(counter)
        const tock = new Audio(tick)
        

        const tickDurationMilliseconds = (60000 / tempo)

        const beatCounter = () => {
            const currentTime = performance.now()
            const elapsed = currentTime - previousTime

            if (elapsed >= tickDurationMilliseconds) {
                isMetronomePlay&& tock.play()

                counter = (counter + 1) % 4
                setMetronome(counter)
                    
                previousTime = currentTime
            }

            if (isPlaying) {
                requestId = requestAnimationFrame(beatCounter)
            }
        }

        let previousTime = performance.now()
        if (isPlaying) {
            requestId = requestAnimationFrame(beatCounter)
        } else {
            counter = 0
            setMetronome(counter)
        }

        return () => {
            if (requestId !== null) {
                cancelAnimationFrame(requestId)
            }
        }
    }, [isPlaying, tempo, isMetronomePlay])



    // TRACKS SYNC
    useEffect(() => {
        const tempTick = 60000 / (tempo * 24)
        setTickDuration(tempTick)
        let requestId = null
        const trackStartTimes = new Array(TRACKS).fill(0)
        let tempPlayhead = new Array(TRACKS).fill(0)

      
        const calcStepDuration = (division, tick) => {
          switch (division) {
            case 6:
              return tick * 1.5
            case 5:
              return tick * 3
            case 4:
              return tick * 6
            case 3:
              return tick * 12
            case 2:
              return tick * 24
            case 1:
              return tick * 48
            case 0:
              return tick * 96
            default:
              break
          }
        }
      
        const sequencerMechanics = (currentTimeStamp) => {
          for (let T = 0; T < TRACKS; T++) {
            const trackStepDuration = calcStepDuration(division[T], tempTick)
            const trackGateDuration = (trackStepDuration / 100) * gatePercentage[T]
            const elapsedTime = currentTimeStamp - trackStartTimes[T]
      
            if (elapsedTime >= trackStepDuration) {
                
                // increment playhead
                tempPlayhead[T] = (playhead[T] + 1) % length[T]
                
                if (!mute[T]) {
                    if (euclideanPattern[T][tempPlayhead[T]] === 1) {
                        let note = parseInt(melody[T][tempPlayhead[T]]) + (parseInt(octave[T]) * 12) + parseInt(semitone[T])
                        note = Math.max(0, Math.min(127, note))
                        // send midi note ON 
                        MIDIOutput.send([NOTE_ON + MIDICH[midiCh[T]], note, 0x7f])
                        //console.log(note)
                        
                        // send midi note OFF
                        MIDIOutput.send([NOTE_OFF + MIDICH[midiCh[T]], note, 0x00], performance.now() + trackGateDuration)
                    }
                }    
                //console.log(`playhead_${0}`, tempPlayhead[0])
                setPlayhead(T, tempPlayhead[T])
                trackStartTimes[T] = currentTimeStamp
            }
          }
      
          requestId = requestAnimationFrame(sequencerMechanics)
        }
      
        if (isPlaying) {
            trackStartTimes.fill(performance.now()) // reset timer with current time
            requestId = requestAnimationFrame(sequencerMechanics) // first iteration since isPLaying is true
            
        }
      
        return () => {
          if (requestId !== null) {
            cancelAnimationFrame(requestId)
          }
        }
      }, [isPlaying, tempo, playhead ])

    /****************************************************************/
    /* calculate notes in the selected scale based on the root note */
    /****************************************************************/
    const calculateScaleNotes = () => {
        // select the scale
        const nameOfScale = scaleNames[scale[currentTrack]]
        const scaleIntervals = musicalScale[nameOfScale]
        console.log('Scale Intervals:', scaleIntervals)
        if (scale[currentTrack] !== 0) { // any scale except chromatic
            // Calculate scale notes based on the root reference and the intervals
            const scaleNotes = scaleIntervals.map(interval => (rootNote[currentTrack] + interval) % 12) 
            console.log('Calculated Scale Notes:', scaleNotes)
            return scaleNotes
        }
    }

    const applyScaleModeToSequence = () => {
        const scaleNotes = calculateScaleNotes()
        //console.log('Root Note:', rootNote)
        //console.log('Scale Notes:', scaleNotes)
        const quantizedSequence = melody[currentTrack].map(note => {
            const noteIndex = note % 12 // Get the note index within the octave range
            const closestNote = findClosestNoteInScale(noteIndex, scaleNotes) // Find the closest note in the scale
            return closestNote
        })

        // Restore the original octave
        const quantizedSequenceWithOctave = quantizedSequence.map((note, index) => {
            const originalOctave = Math.floor(melody[currentTrack][index] / 12) // Calculate the original octave
            return note + (originalOctave * 12) // Add the original octave to the quantized note
        })

        //console.log('Quantized Sequence with Octave: ', quantizedSequenceWithOctave)

        // Update the melody with the quantized melody
        quantizedSequenceWithOctave.forEach((note, index) => {
            setNote_scale(currentTrack, index, note)
        })
    }

    // Function to find the closest note in the scale
    const findClosestNoteInScale = (noteIndex, scaleNotes, mode) => {
        let closestNote = scaleNotes[0]
        let minDifference = Math.abs(noteIndex - closestNote)
    
        for (let i = 1; i < scaleNotes.length; i++) {
            const difference = Math.abs(noteIndex - scaleNotes[i])
            if (difference < minDifference) {
                minDifference = difference
                closestNote = scaleNotes[i]
            } else if (difference === minDifference && modeIntervals[mode]?.includes(scaleNotes[i])) {
                closestNote = scaleNotes[i]
            }
        }
        return closestNote
    }

    // note quantizer
    useEffect(() => {
        if (melody[currentTrack] && scale[currentTrack] && musicalScale) {
            applyScaleModeToSequence()
        }
    }, [scale, rootNote, currentTrack, melody])

    /**********************/
    /* handle play button */
    /**********************/
    const handlePlay = () => {
        if (isPlaying) {
            setIsPlaying(false) 

            for (let i=0; i < TRACKS; i++) { // reset playhead
                MIDIOutput.send([CC + MIDICH[midiCh[i]], ALLSOUNDOFF, 0x00])// send note OFF
                setPlayhead(i, -1)
            } 
        } else {
            setIsPlaying(true)
            setIsRecording(false)  // prevent record to be true
        }
    }

    /***********************/
    /* handle record state */
    /***********************/
    const handleRecord = () => {
        if (isPlaying) { 
            setIsRecording(false)
        } else if (isRecording) {
            setIsRecording(false)
            for (let i=0; i < TRACKS; i++) { // reset playhead
                setPlayhead(i, -1)
            }
        } else {
            setIsRecording(true)
            for (let i=0; i < TRACKS; i++) { // set playhead to 0 waiting an input
                setPlayhead(i, 0)
            }
        }
    }

    /**************************/
    /* event listener 'Space' */
    /**************************/
    useEffect(() => {
        window.addEventListener("keydown", handleSpaceKey)  
    
        return () => {
          window.removeEventListener("keydown", handleSpaceKey)
        }
      }, [isPlaying])  
    
    // handle space key press
    const handleSpaceKey = (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (event.key === ' ') {
            handlePlay()
        }
    }

    /************************************/
    /* handle midi out device selection */
    /************************************/
    const selectMidiOutDevice = useCallback((selectedDevice) => {
        setMIDIOutput(selectedDevice)
        //console.log('selected midi: ', selectedDevice)
        if (selectedDevice.id) {
            sessionStorage.setItem('storedMidiOutDevice', selectedDevice.id)
        }
    }, [])

    /************************************/
    /* handle midi in device selection */
    /************************************/
    const selectMidiInDevice = useCallback((selectedDevice) => {
        setMIDIInput(selectedDevice)
        //console.log('selected midi: ', selectedDevice)
        if (selectedDevice.id) {
            sessionStorage.setItem('storedMidiInDevice', selectedDevice.id)
        }
    }, [])

    /**************************************/
    /* open/close dropdown menu Sequencer */
    /**************************************/
    const handleSequenceMenu = () => {
        setSequenceMenuState(!sequenceMenuState)
    }

    /**************************************/
    /* open/close dropdown menu Keyboard */
    /**************************************/
    const handleKeyboardMenu = () => {
        setKeyboardMenuState(!keyboardMenuState)
    }

    /**************************************/
    /* Metronome Play/Stop Functionality  */
    /**************************************/
    const handleMetronomeTick = () => {
        setIsMetronomePlay(!isMetronomePlay)
    }

    return (
        <div className="App">
            <section id='headerSection'>
                <div id='heroWrapper'>
                    <h1>meloclidean</h1>
                    <h3>A 4 tracks melodic euclidean sequencer in your browser with midi OUT.</h3>
                </div>              
                <div id='transportWrapper'>
                        <PlayButton isPlaying={isPlaying} handlePlay={handlePlay} handleSpaceKey={handleSpaceKey}/>
                        <StopButton isPlaying={isPlaying} handlePlay={handlePlay}/>
                    <RecButton isRecording={isRecording} handleRecord={handleRecord} />
                    <Tempo />
                    <MetronomeIcon metronome={metronome} isPlaying={isPlaying} handleMetronomeTick={handleMetronomeTick}/>
                </div>
            </section>
            <main>
                <section id='tracksSection'>
                    <TracksMonitor />
                </section>
                <section id='parametersSection'>
                    <div id='melodyCol'> 
                        <div className="colTitleWrapper">
                            <MelodyIcon />
                            <h4>melody</h4>
                        </div>
                        <div className='fadersWrapper'>
                            <Transpose />
                            <Octave />
                        </div>
                    </div>
                    <div id='euclideanCol'> 
                        <div className="colTitleWrapper">
                            <EuclideanIcon />
                            <h4>euclidean rhythm</h4>
                        </div>
                        <div className='fadersWrapper'>
                            <Length />
                            <Pulses />
                            <Offset />
                            <GateLength />
                            <Division />
                        </div>
                    </div>
                    <div id='routingCol'> 
                        <div className="colTitleWrapper">
                            <RoutingIcon />
                            <h4>routing</h4>
                        </div>
                        <div className='fadersWrapper'>
                            <MidiOutSelect onSelect={selectMidiOutDevice} />
                            <MidiInSelect onSelect={selectMidiInDevice} />
                            <MidiChannel />
                        </div>
                    </div>
                </section>
                <section id='sequencerSection'>
                <div className="colTitleWrapper" onClick={handleSequenceMenu} >
                    <SequenceIcon />
                    <h4>sequence</h4>
                    <ChevronIcon sequenceMenuState={sequenceMenuState}/>
                </div>
                {sequenceMenuState?
                    <Sequencer isRecording={isRecording} isPlaying={isPlaying} gateLength={gateLength} MIDIOutput={MIDIOutput}/>
                :
                    null
                }
                </section>
                <section id='keyboardSection'>
                    <div className="colTitleWrapper" onClick={handleKeyboardMenu} >
                        <KeyboardIcon />
                        <h4>keyboard</h4>
                        <ChevronIcon keyboardMenuState={keyboardMenuState}/>
                    </div>
                        {keyboardMenuState?
                            <Keyboard isPlaying={isPlaying} isRecording={isRecording} MIDIOutput={MIDIOutput} MIDIInput={MIDIInput}gateLength={gateLength}/>
                        :
                            null}

                </section>
            </main>
        </div>
    )
}